import { useCallback, useMemo, useRef, useState } from "react";
import { Gizmo, useGizmo, IGizmoEvent } from "flowy-3-core";
import type { IReaderConfig } from "alce-id-reader";
import { IdCredentialField } from "alce-id-reader/react";
import type {
  BackReadResultWithBase64,
  FrontReadResultWithBase64,
} from "alce-id-reader/react";
import "alce-id-reader/style.css";
import { GizmoWrapper } from "../../utils";
import getBinderResult from "./alce/get-binder-result";
import buildSessionSummary from "./alce/build-session-summary";

type IdOcrFiles = { front?: File; back?: File };
type UploadSide = "front" | "back";
type PersonalIdImagePrefix =
  | "fullDocumentFrontImage"
  | "fullDocumentBackImage"
  | "faceImage";

function emitPersonalIdImage(
  gizmo: Gizmo,
  prefix: PersonalIdImagePrefix,
  value: File | undefined
) {
  const config = gizmo.getConfig?.() as
    | {
        fid?: string;
        uid?: string;
        ops?: { idOcrField?: { personalId?: Record<string, string | undefined> } };
      }
    | undefined;
  const personalId = config?.ops?.idOcrField?.personalId;
  if (!personalId || !config) return;

  const fid = personalId[`${prefix}GizmoFid`];
  const uid = personalId[`${prefix}GizmoUid`];
  if (!fid && !uid) return;

  const event: {
    type: string;
    src: { type: string; gizmoFid?: string; gizmoUid?: string };
    dest: { type: string; gizmoFid?: string; gizmoUid?: string };
    data?: { gizmoSetData: { value: File } };
  } = {
    type: "gizmo.set-data",
    src: { type: "gizmo", gizmoFid: config.fid, gizmoUid: config.uid },
    dest: fid
      ? { type: "gizmo", gizmoFid: fid }
      : { type: "gizmo", gizmoUid: uid },
  };
  if (value) {
    event.data = { gizmoSetData: { value } };
    gizmo.getEvents().next(event as IGizmoEvent);
  }
}

const IdOcrFieldAlce = ({ gizmo }: { gizmo: Gizmo }) => {
  const { features, errors, binder } = useGizmo({ gizmo });

  const readerConfig = useMemo(() => {
    const cfg = gizmo.getConfig?.() as
      | { ops?: { idOcrField?: { readerConfig?: IReaderConfig } } }
      | undefined;
    const base = cfg?.ops?.idOcrField?.readerConfig;
    const withLicense = {
      licenseKey: "license-key",
      licenseServerUrl: "/__alce-license",
      ...base,
    };
    const onlyFrontFromBinder = !!(
      binder as { getOnlyFront?: () => boolean }
    ).getOnlyFront?.();
    if (!onlyFrontFromBinder) return withLicense;
    return { ...withLicense, askBack: false };
  }, [binder, gizmo]);

  const askBack = readerConfig?.askBack !== false;
  const onlyFront = !askBack;

  const alceStagingRef = useRef<{
    front?: FrontReadResultWithBase64;
    back?: BackReadResultWithBase64;
    sessionSummary?: unknown;
  }>({});
  const filesSnapshotForReplaceRef = useRef<IdOcrFiles>({});
  const imageReplaceSessionRef = useRef<{
    active: boolean;
    replaced: { front: boolean; back: boolean };
  }>({ active: false, replaced: { front: false, back: false } });

  const [preservedOcrForImageReplace, setPreservedOcrForImageReplace] =
    useState<unknown>(null);

  const getExistingOcrResult = useCallback(() => {
    const data = gizmo.getData?.() as
      | { id?: { ocrResult?: unknown } }
      | Array<{ id?: { ocrResult?: unknown } }>
      | undefined;
    if (Array.isArray(data)) return data[0]?.id?.ocrResult;
    return data?.id?.ocrResult;
  }, [gizmo]);

  const commitAlceToBinder = useCallback(async () => {
    const staging = alceStagingRef.current;
    const front = staging.front;
    const back = staging.back;
    const setOcrResult = (
      binder as {
        setOcrResult?: (
          payload: unknown,
          dataForUserEvent?: unknown
        ) => Promise<void>;
      }
    ).setOcrResult;
    if (!setOcrResult) {
      console.warn("[ALCE] binder.setOcrResult no disponible");
      return;
    }

    if (onlyFront) {
      if (!front) return;
    } else if (!front || !back) {
      return;
    }

    try {
      const binderPayload = await getBinderResult({
        idReadingData: {
          ocrResults: front?.ocrRows,
          mrzData: back?.mrzData,
          frontImageDataUrl: front?.cropBase64,
          backImageDataUrl: back?.cropBase64,
          frontCaptured: !!front,
          backCaptured: !!back,
        },
      });
      const sessionSummary =
        staging.sessionSummary ??
        back?.sessionSummary ??
        buildSessionSummary(
          front as Parameters<typeof buildSessionSummary>[0],
          back as Parameters<typeof buildSessionSummary>[1],
          (readerConfig as { source?: "web" | "mobile" }).source
        );
      alceStagingRef.current = { ...alceStagingRef.current, sessionSummary };
      console.log("[ALCE] sessionSummary en ocrResult:", sessionSummary);
      const ocrPayload = sessionSummary
        ? { ...(binderPayload as object), sessionSummary }
        : binderPayload;
      await setOcrResult(ocrPayload, sessionSummary);

      if (front?.cropBlob) {
        const frontFile = new File([front.cropBlob], "id-front.jpg", {
          type: front.cropBlob.type || "image/jpeg",
        });
        emitPersonalIdImage(gizmo, "fullDocumentFrontImage", frontFile);
      }
      if (back?.cropBlob) {
        const backFile = new File([back.cropBlob], "id-back.jpg", {
          type: back.cropBlob.type || "image/jpeg",
        });
        emitPersonalIdImage(gizmo, "fullDocumentBackImage", backFile);
      }
    } catch (err) {
      console.error("[ALCE] binder write failed", err);
    }
  }, [binder, gizmo, onlyFront, readerConfig]);

  const persistReplacedImage = useCallback(
    async (side: UploadSide, cropped: File, preservedOcr: unknown) => {
      const summary = alceStagingRef.current.sessionSummary;
      const ocrResult =
        summary && preservedOcr && typeof preservedOcr === "object"
          ? { ...(preservedOcr as object), sessionSummary: summary }
          : preservedOcr;
      const setFile = (
        binder as {
          setFile?: (t: "front" | "back", f: File, o?: unknown) => Promise<void>;
        }
      ).setFile;
      const getFiles = (binder as { getFiles?: () => IdOcrFiles }).getFiles;
      const filesSub = binder as { files?: { next: (v: object) => void } };
      const files = getFiles?.() || {};

      const snapshot = filesSnapshotForReplaceRef.current;
      if (side === "front" && !files.back && snapshot.back) {
        files.back = snapshot.back;
      }
      if (side === "back" && !files.front && snapshot.front) {
        files.front = snapshot.front;
      }

      if (setFile) {
        await setFile(side, cropped, ocrResult);
      } else {
        files[side] = cropped;
      }

      const session = imageReplaceSessionRef.current;
      session.replaced[side] = true;
      const latest = getFiles?.() || files;
      if (session.replaced.front && session.replaced.back) {
        session.active = false;
        setPreservedOcrForImageReplace(null);
      }
      filesSub.files?.next({
        ...(session.replaced.front && latest.front
          ? { front: latest.front }
          : {}),
        ...(session.replaced.back && latest.back ? { back: latest.back } : {}),
      });
    },
    [binder]
  );

  const handleFrontResult = useCallback(
    async (result: FrontReadResultWithBase64) => {
      const ocrToPreserve =
        preservedOcrForImageReplace ??
        (imageReplaceSessionRef.current.active
          ? getExistingOcrResult()
          : undefined);

      if (ocrToPreserve && result.cropBlob) {
        const cropped = new File([result.cropBlob], "id-front.jpg", {
          type: result.cropBlob.type || "image/jpeg",
        });
        await persistReplacedImage("front", cropped, ocrToPreserve);
        return;
      }

      imageReplaceSessionRef.current = {
        active: false,
        replaced: { front: false, back: false },
      };
      setPreservedOcrForImageReplace(null);
      alceStagingRef.current = { ...alceStagingRef.current, front: result };
      await commitAlceToBinder();
    },
    [
      commitAlceToBinder,
      getExistingOcrResult,
      persistReplacedImage,
      preservedOcrForImageReplace,
    ]
  );

  const handleBackResult = useCallback(
    async (result: BackReadResultWithBase64) => {
      const ocrToPreserve =
        preservedOcrForImageReplace ??
        (imageReplaceSessionRef.current.active
          ? getExistingOcrResult()
          : undefined);

      if (ocrToPreserve && result.cropBlob) {
        if (result.sessionSummary) {
          alceStagingRef.current = {
            ...alceStagingRef.current,
            sessionSummary: result.sessionSummary,
          };
        }
        const cropped = new File([result.cropBlob], "id-back.jpg", {
          type: result.cropBlob.type || "image/jpeg",
        });
        await persistReplacedImage("back", cropped, ocrToPreserve);
        return;
      }

      imageReplaceSessionRef.current = {
        active: false,
        replaced: { front: false, back: false },
      };
      setPreservedOcrForImageReplace(null);
      alceStagingRef.current = {
        ...alceStagingRef.current,
        back: result,
        sessionSummary:
          result.sessionSummary ?? alceStagingRef.current.sessionSummary,
      };
      await commitAlceToBinder();
    },
    [
      commitAlceToBinder,
      getExistingOcrResult,
      persistReplacedImage,
      preservedOcrForImageReplace,
    ]
  );

  const handleSessionSummary = useCallback(
    (summary: unknown) => {
      alceStagingRef.current = {
        ...alceStagingRef.current,
        sessionSummary: summary,
      };
      const data = gizmo.getData?.() as
        | Array<{
            id?: {
              ocrResult?: { sessionSummary?: unknown } & Record<string, unknown>;
            };
          }>
        | undefined;
      if (
        !Array.isArray(data) ||
        !data[0]?.id?.ocrResult ||
        data[0].id.ocrResult.sessionSummary
      )
        return;
      const [first, ...rest] = data;
      const ocrResult = first?.id?.ocrResult;
      if (!ocrResult) return;
      void gizmo.setData([
        {
          ...first,
          id: {
            ...first.id,
            ocrResult: { ...ocrResult, sessionSummary: summary },
          },
        },
        ...rest,
      ] as Parameters<Gizmo["setData"]>[0]);
    },
    [gizmo]
  );

  const handleClear = useCallback(async () => {
    try {
      const existingOcr = getExistingOcrResult();

      if (existingOcr) {
        setPreservedOcrForImageReplace(existingOcr);
        const files = (
          binder as { getFiles?: () => IdOcrFiles }
        ).getFiles?.();
        filesSnapshotForReplaceRef.current = {
          front: files?.front,
          back: files?.back,
        };
        imageReplaceSessionRef.current = {
          active: true,
          replaced: { front: false, back: false },
        };
        if (files) {
          delete files.front;
          delete files.back;
        }
        (binder as { files?: { next: (value: object) => void } }).files?.next(
          {}
        );
        alceStagingRef.current = {};
        return;
      }

      setPreservedOcrForImageReplace(null);
      filesSnapshotForReplaceRef.current = {};
      imageReplaceSessionRef.current = {
        active: false,
        replaced: { front: false, back: false },
      };
      await gizmo.setData(undefined);
      const files = (binder as { getFiles?: () => IdOcrFiles }).getFiles?.();
      if (files) {
        delete files.front;
        delete files.back;
      }
      (binder as { files?: { next: (value: object) => void } }).files?.next({});
      alceStagingRef.current = {};
    } catch (err) {
      console.error("[ALCE] clear reading failed:", err);
    }
  }, [binder, getExistingOcrResult, gizmo]);

  return (
    <GizmoWrapper features={features} errors={errors}>
      <IdCredentialField
        config={readerConfig}
        progressColor="#E23151"
        onFrontResult={(result) => {
          void handleFrontResult(result);
        }}
        onBackResult={
          onlyFront
            ? undefined
            : (result) => {
                void handleBackResult(result);
              }
        }
        onSessionSummary={handleSessionSummary}
        onClear={() => {
          void handleClear();
        }}
        onError={(err) => {
          console.error("[ALCE]", err);
        }}
      />
    </GizmoWrapper>
  );
};

export default IdOcrFieldAlce;
